<template>
  <div>
    <div class="bg-background-light min-h-screen flex flex-col font-display antialiased text-[#181611]">

      <div class="flex-grow flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div class="w-full max-w-[480px] bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#e5e7eb] overflow-hidden relative z-10">

          <!-- Top Decoration Bar -->
          <div class="h-2 w-full bg-secondary"></div>

          <div class="p-8 sm:p-10">

            <!-- Logo -->
            <div class="flex flex-col items-center text-center mb-8">
              <div class="flex items-center justify-center gap-3 mb-6">
                <img src="/abc_logo.png" alt="ABC Tax Advisory" class="h-12 w-auto object-contain" />
                <div class="flex flex-col text-left">
                  <span class="text-lg font-bold text-secondary leading-tight">ABC Tax Advisory</span>
                  <span class="text-xs text-slate-400 font-medium">Client Portal</span>
                </div>
              </div>
              <h2 class="text-xl font-semibold text-slate-800">
                {{ step === 'email' ? 'Secure Access' : 'Enter Your Code' }}
              </h2>
              <p class="text-gray-500 text-sm mt-2">
                {{ step === 'email'
                  ? 'Enter your email to continue'
                  : `We sent a 6-digit code to ${maskedEmail}` }}
              </p>
            </div>

            <!-- Checking spinner -->
            <div v-if="checkingRole" class="flex flex-col items-center gap-3 py-6">
              <svg class="animate-spin h-8 w-8 text-secondary" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="text-sm text-gray-500">Verifying your access...</p>
            </div>

            <div v-else>

              <!-- STEP 1: EMAIL -->
              <div v-if="step === 'email'" class="flex flex-col gap-5">
                <div>
                  <label class="block text-xs font-semibold text-slate-600 mb-1.5">Email Address</label>
                  <input
                    v-model="email"
                    type="email"
                    placeholder="Enter your email address"
                    @keydown.enter="handleContinue"
                    class="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition-colors"
                    autofocus />
                </div>

                <button
                  @click="handleContinue"
                  :disabled="loading || !email.trim()"
                  class="w-full flex justify-center items-center py-3 px-4 rounded-lg text-sm font-bold text-white bg-secondary hover:bg-secondary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed gap-2">
                  <svg v-if="loading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{{ loading ? 'Checking...' : 'Continue' }}</span>
                  <span v-if="!loading" class="material-symbols-outlined !text-[18px]">arrow_forward</span>
                </button>

                <div v-if="error" class="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
                  <span class="material-symbols-outlined !text-[18px]">error</span>
                  {{ error }}
                </div>

                <div v-if="!error" class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div class="flex gap-3">
                    <span class="material-symbols-outlined !text-[20px] text-blue-600 flex-shrink-0">info</span>
                    <div class="text-sm text-blue-600">
                      <p class="font-semibold mb-1">How it works</p>
                      <p class="text-xs">Admins will be redirected to Microsoft login. Clients will receive a secure 6-digit code by email.</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- STEP 2: OTP -->
              <div v-else-if="step === 'otp'" class="flex flex-col gap-5">

                <div class="flex justify-center gap-2">
                  <input
                    v-for="(digit, index) in otpDigits"
                    :key="index"
                    :ref="`otp${index}`"
                    v-model="otpDigits[index]"
                    type="text"
                    inputmode="numeric"
                    maxlength="1"
                    @input="handleOtpInput(index)"
                    @keydown.backspace="handleOtpBackspace(index)"
                    @keydown.enter="handleVerify"
                    @paste.prevent="handleOtpPaste"
                    class="w-12 h-14 text-center text-xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2 transition-all"
                    :class="otpError
                      ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-200'
                      : otpDigits[index]
                        ? 'border-secondary bg-secondary/5 focus:border-secondary focus:ring-secondary/20'
                        : 'border-slate-200 focus:border-secondary focus:ring-secondary/20'" />
                </div>

                <button
                  @click="handleVerify"
                  :disabled="verifying || otpDigits.join('').length < 6"
                  class="w-full flex justify-center items-center py-3 px-4 rounded-lg text-sm font-bold text-secondary bg-primary hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed gap-2 shadow-sm">
                  <svg v-if="verifying" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span class="material-symbols-outlined !text-[18px]" v-else>verified_user</span>
                  {{ verifying ? 'Verifying...' : 'Verify & Login' }}
                </button>

                <div v-if="otpError" class="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
                  <span class="material-symbols-outlined !text-[18px]">error</span>
                  {{ otpError }}
                </div>

                <div class="flex items-center justify-between text-sm">
                  <button @click="goBack" class="text-slate-500 hover:text-secondary transition-colors flex items-center gap-1">
                    <span class="material-symbols-outlined !text-[16px]">arrow_back</span>
                    Back
                  </button>
                  <button
                    @click="resendCode"
                    :disabled="resendCooldown > 0 || resending"
                    class="text-secondary font-semibold hover:underline disabled:text-slate-400 disabled:no-underline transition-colors">
                    {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : resending ? 'Sending...' : 'Resend Code' }}
                  </button>
                </div>

                <div class="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-700 text-xs">
                  <span class="material-symbols-outlined !text-[16px]">timer</span>
                  This code expires in 15 minutes. Do not share it with anyone.
                </div>
              </div>

            </div>

            <!-- Security Badge -->
            <div class="mt-8 pt-6 border-t border-gray-100 flex items-center justify-center gap-2 text-gray-400">
              <span class="material-symbols-outlined !text-[16px]">lock_clock</span>
              <span class="text-xs font-medium uppercase tracking-wider">256-bit SSL Secured Connection</span>
            </div>
          </div>
        </div>

        <!-- Background Decor -->
        <div class="fixed top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
          <div class="absolute -top-[10%] -right-[5%] w-[40%] h-[40%] opacity-10 rounded-full blur-[100px]" style="background-color: #e6af2d;"></div>
          <div class="absolute -bottom-[10%] -left-[5%] w-[40%] h-[40%] opacity-5 rounded-full blur-[100px]" style="background-color: #282F44;"></div>
        </div>
      </div>

      <footer class="py-6 text-center text-xs text-gray-400">
        <p>© {{ new Date().getFullYear() }} ABC Tax Advisory Pty Ltd — ABN 17 666 126 870. All rights reserved.</p>
      </footer>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoginPage',

  data() {
    return {
      step: 'email',
      email: '',
      otpDigits: ['', '', '', '', '', ''],
      loading: false,
      verifying: false,
      resending: false,
      checkingRole: false,
      error: '',
      otpError: '',
      resendCooldown: 0,
      resendTimer: null
    };
  },

  computed: {
    maskedEmail() {
      if (!this.email) return '';
      const [user, domain] = this.email.split('@');
      if (!domain) return this.email;
      const masked = user.length <= 2 ? user[0] + '***' : user[0] + '***' + user[user.length - 1];
      return `${masked}@${domain}`;
    }
  },

  async mounted() {
    try {
      const isAuthenticated = this.$sharepoint.isAuthenticated();
      if (isAuthenticated) await this.resolveAdminRole();
    } catch (e) { /* not authenticated yet */ }
  },

  beforeUnmount() {
    if (this.resendTimer) clearInterval(this.resendTimer);
  },

  methods: {

    async handleContinue() {
      const email = this.email.trim().toLowerCase();
      if (!email) return;
      this.loading = true;
      this.error = '';
      try {
        // Check if admin via SharePoint User Roles list
        const adminCheck = await fetch('/.netlify/functions/check-admin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        const adminData = await adminCheck.json();
        if (adminData.isAdmin) {
          await this.$sharepoint.login();
          return;
        }
        // Call Netlify Function to check email + generate code
        const res = await fetch('/.netlify/functions/check-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        const data = await res.json();
        if (!data.exists) {
          this.error = 'No account found for this email. Please contact your advisor.';
          return;
        }
        // Code generated — Power Automate sends it via email
        this.step = 'otp';
        this.startResendCooldown();
        this.$nextTick(() => this.$refs.otp0?.[0]?.focus());
      } catch (err) {
        console.error(err);
        this.error = 'Something went wrong. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    async handleVerify() {
      const code = this.otpDigits.join('');
      if (code.length < 6) return;
      this.verifying = true;
      this.otpError = '';
      try {
        // Call Netlify Function to verify code
        const res = await fetch('/.netlify/functions/verify-code', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: this.email.trim().toLowerCase(), code })
        });
        const data = await res.json();
        if (!data.valid) {
          this.otpError = data.reason;
          this.otpDigits = ['', '', '', '', '', ''];
          this.$nextTick(() => this.$refs.otp0?.[0]?.focus());
          return;
        }
        // Save signed session token — identity is locked inside the server signature
        sessionStorage.setItem('clientSession', data.sessionToken);
        this.$router.push('/client/dashboard');
      } catch (err) {
        console.error(err);
        this.otpError = 'Error verifying code. Please try again.';
      } finally {
        this.verifying = false;
      }
    },

    async resendCode() {
      if (this.resendCooldown > 0 || this.resending) return;
      this.resending = true;
      this.otpError = '';
      this.otpDigits = ['', '', '', '', '', ''];
      try {
        const res = await fetch('/.netlify/functions/check-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: this.email.trim().toLowerCase() })
        });
        const data = await res.json();
        if (!data.codeSent) throw new Error('Failed to resend');
        this.startResendCooldown();
        this.$nextTick(() => this.$refs.otp0?.[0]?.focus());
      } catch (err) {
        this.otpError = 'Failed to resend. Please try again.';
      } finally {
        this.resending = false;
      }
    },

    startResendCooldown() {
      this.resendCooldown = 60;
      if (this.resendTimer) clearInterval(this.resendTimer);
      this.resendTimer = setInterval(() => {
        this.resendCooldown--;
        if (this.resendCooldown <= 0) clearInterval(this.resendTimer);
      }, 1000);
    },

    handleOtpInput(index) {
      if (!/^\d*$/.test(this.otpDigits[index])) {
        this.otpDigits[index] = '';
        return;
      }
      if (this.otpDigits[index] && index < 5) {
        this.$nextTick(() => this.$refs[`otp${index + 1}`]?.[0]?.focus());
      }
      if (this.otpDigits.join('').length === 6) {
        this.$nextTick(() => this.handleVerify());
      }
    },

    handleOtpBackspace(index) {
      if (!this.otpDigits[index] && index > 0) {
        this.$nextTick(() => this.$refs[`otp${index - 1}`]?.[0]?.focus());
      }
    },

    handleOtpPaste(event) {
      const paste = (event.clipboardData || window.clipboardData)
        .getData('text').replace(/\D/g, '').slice(0, 6);
      if (!paste) return;
      paste.split('').forEach((char, i) => { if (i < 6) this.otpDigits[i] = char; });
      this.$nextTick(() => {
        const nextIndex = Math.min(paste.length, 5);
        this.$refs[`otp${nextIndex}`]?.[0]?.focus();
        if (paste.length === 6) this.handleVerify();
      });
    },

    goBack() {
      this.step = 'email';
      this.otpDigits = ['', '', '', '', '', ''];
      this.otpError = '';
      if (this.resendTimer) clearInterval(this.resendTimer);
      this.resendCooldown = 0;
    },

    async resolveAdminRole() {
      this.checkingRole = true;
      // Safety: never spin forever
      const timeout = setTimeout(() => {
        this.checkingRole = false;
        this.error = 'Verification timed out. Please try again.';
      }, 8000);
      try {
        const user = this.$sharepoint.getCurrentUser();
        const email = (user?.username || '').toLowerCase().trim();
        if (!email) {
          clearTimeout(timeout);
          this.checkingRole = false;
          this.error = 'Could not retrieve account email. Please try again.';
          return;
        }
        // Verify against SharePoint User Roles list
        const res = await fetch('/.netlify/functions/check-admin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        const data = await res.json();
        clearTimeout(timeout);
        if (data.isAdmin) {
          await this.$router.push('/admin/clients');
          this.checkingRole = false;
        } else {
          this.error = `Account (${email}) is not registered as an admin.`;
          this.checkingRole = false;
        }
      } catch (err) {
        clearTimeout(timeout);
        this.error = 'Error verifying access. Please try again.';
        this.checkingRole = false;
      }
    }
  }
};
</script>

<style>
body { font-family: 'Public Sans', sans-serif; }
.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}
</style>